// Port of SuiteModel/WordFrequency.swift: how often each word appears, most frequent first. Words compare without case
// and show in their most common spelling (names keep their capitals); common English words are hidden by default.
// Exact without grouping. Grouping forms under their dictionary form ("walk": walk, walked, walking) is Apple-only
// (`NLTagger` lemmas): it runs only with a real `LanguageTagger` injected; otherwise the count is ungrouped and
// `groupingUnavailable` says why.
import { plainText } from '../proseMarkdown.ts';
import { swiftCompare } from '../model/order.ts';
import { graphemes } from '../unicode.ts';
import { PROSE_WORD } from './proseWords.ts';
import { charIsUppercase } from './scalars.ts';
import { GROUP_FORMS_APPLE_ONLY, type LanguageTagger } from './tagger.ts';

export interface WordFrequencyEntry {
  /** Its most common spelling, or its dictionary form when grouped. */
  word: string;
  count: number;
  /** The spellings counted together, most common first. */
  forms: string[];
}

export interface WordFrequency {
  /** Every word counted, most frequent first; ties alphabetical. */
  entries: WordFrequencyEntry[];
  /** All the words in the text, common ones included. */
  totalWords: number;
  /** Different words (forms separately), common ones included. */
  distinctWords: number;
  /** Set when grouping was asked for but no real tagger was given. */
  groupingUnavailable?: string;
}

/** `WordFrequency.common`. */
export const COMMON_WORDS: ReadonlySet<string> = new Set(`
  a about above after again against all am an and any are aren't as at be because been before being below
  between both but by can can't cannot could couldn't did didn't do does doesn't doing don't down during each
  few for from further had hadn't has hasn't have haven't having he he'd he'll he's her here here's hers herself
  him himself his how how's i i'd i'll i'm i've if in into is isn't it it's its itself let's me more most mustn't
  my myself no nor not of off on once only or other ought our ours ourselves out over own same shan't she she'd
  she'll she's should shouldn't so some such than that that's the their theirs them themselves then there
  there's these they they'd they'll they're they've this those through to too under until up very was wasn't
  we we'd we'll we're we've were weren't what what's when when's where where's which while who who's whom why
  why's with won't would wouldn't you you'd you'll you're you've your yours yourself yourselves
  said say says just got get like back one also will`.split(/\s+/).filter(Boolean));

export const perThousand = (f: WordFrequency, e: WordFrequencyEntry) => (f.totalWords === 0 ? 0 : (e.count * 1000) / f.totalWords);

/** `WordFrequency.count`. */
export function countWords(texts: readonly string[], o: { excludeCommon?: boolean; groupForms?: boolean; tagger?: LanguageTagger | null } = {}): WordFrequency {
  const excludeCommon = o.excludeCommon ?? true;
  const tagger = o.groupForms && o.tagger?.exact ? o.tagger : null;
  const groupForms = tagger !== null;
  const spellings = new Map<string, Map<string, number>>(); // key → spelling → count
  let total = 0;
  for (const text of texts) {
    const plain = plainText(text);
    for (const m of plain.matchAll(PROSE_WORD)) {
      const spelling = m[0].replaceAll('’', "'");
      const lower = spelling.toLowerCase();
      total++;
      let key = lower;
      if (tagger) {
        const lemma = tagger.lemma(plain, [m.index!, m.index! + m[0].length])?.toLowerCase();
        if (lemma) key = lemma;
      }
      let forms = spellings.get(key);
      if (!forms) spellings.set(key, (forms = new Map()));
      forms.set(spelling, (forms.get(spelling) ?? 0) + 1);
    }
  }
  const distinct = new Set([...spellings.values()].flatMap((f) => [...f.keys()].map((s) => s.toLowerCase()))).size;
  const entries: WordFrequencyEntry[] = [];
  for (const [key, forms] of spellings) {
    if (excludeCommon && (COMMON_WORDS.has(key) || [...forms.keys()].every((s) => COMMON_WORDS.has(s.toLowerCase())))) continue;
    // Case-insensitive forms, each shown in its most common spelling.
    const byLower = new Map<string, { spelling: string; count: number; best: number }>();
    for (const [spelling, n] of forms) {
      const lower = spelling.toLowerCase();
      const entry = byLower.get(lower) ?? { spelling, count: 0, best: 0 };
      entry.count += n;
      if (n > entry.best) { entry.spelling = spelling; entry.best = n; }
      byLower.set(lower, entry);
    }
    const ordered = [...byLower.values()].sort((a, b) => b.count - a.count || swiftCompare(a.spelling, b.spelling));
    const count = ordered.reduce((n, e) => n + e.count, 0);
    // Grouped: the dictionary form, unless it's only the spelling in lower case (keeps "Phil").
    let shown = groupForms && (ordered.length > 1 || key !== ordered[0].spelling.toLowerCase()) ? key : ordered[0].spelling;
    // A name is always capitalised ("Gary" for Gary, Gary's), so its group is too.
    if (groupForms && ordered.every((e) => charIsUppercase(graphemes(e.spelling)[0]))) {
      const g = graphemes(shown);
      shown = (g[0] ?? '').toUpperCase() + g.slice(1).join('');
    }
    entries.push({ word: shown, count, forms: ordered.map((e) => e.spelling) });
  }
  entries.sort((a, b) => b.count - a.count || swiftCompare(a.word.toLowerCase(), b.word.toLowerCase()));
  return { entries, totalWords: total, distinctWords: distinct, ...(o.groupForms && !groupForms ? { groupingUnavailable: GROUP_FORMS_APPLE_ONLY } : {}) };
}

/** `csv()`: word, count, per 1,000 words, forms (RFC 4180 quoting). */
export function frequencyCSV(f: WordFrequency): string {
  const field = (s: string) => (/[,"\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s);
  return ['word,count,per 1000 words,forms', ...f.entries.map((e) =>
    [field(e.word), String(e.count), perThousand(f, e).toFixed(2), field(e.forms.join(' '))].join(','))].join('\n') + '\n';
}
